import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { InfoHint } from "../components/InfoHint";

import { api, formatApiError } from "../lib/api";
import EmailPreferencesCard from "../components/EmailPreferencesCard";
import EmailPolicyBanner from "../components/EmailPolicyBanner";
import EmailSenderCard from "../components/EmailSenderCard";
import ModuliFacoltativiCard from "../components/ModuliFacoltativiCard";
import LicenzeCondominiV2 from "../components/LicenzeCondominiV2";
import ProfileStudioCard from "../components/ProfileStudioCard";
import LogoFirmaCard from "../components/LogoFirmaCard";
import AutomazioniNotificheCard from "../components/AutomazioniNotificheCard";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  SlidersHorizontal, UserPlus, Trash2, KeyRound, Power, AtSign,
  Building2, ShieldCheck, Mail, Check, X, Users,
  Save, Bell, FileSpreadsheet, Download, Heart, Play, Loader2,
  Image as ImageIcon, PenLine, Package, Receipt, MailOpen, Palette, BellOff,
} from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function Row({ label, ok, text }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? <Check className="w-4 h-4 text-green-600 shrink-0" /> : <X className="w-4 h-4 text-red-600 shrink-0" />}
      <span className="text-slate-600">{label}{text && !ok ? `: ${text}` : ""}</span>
    </div>
  );
}

function Field({ label, required, ...props }) {
  return (
    <div className="mb-3">
      <label className="text-sm font-semibold text-slate-600">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      <Input className="h-11 mt-1" {...props} />
    </div>
  );
}

export default function ControlPanel() {
  // Iter122av · discrimina admin Studio Izzo (o super_admin) da admin studi terzi
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === "super_admin" || user?.tenant_id === "studio";
  // Iter122dv · Menu a bandiera "Impostazioni & Account"
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "notifiche-email";
  const [activeTab, setActiveTab] = useState(defaultTab);
  // Iter122bb · trial/richieste/proposta spostati esclusivamente in SuperAdmin (/superadmin).
  const [data, setData] = useState({ collaboratori: [], limiti: {}, sezioni_opzionali: [] });
  const [collab, setCollab] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailTestRes, setEmailTestRes] = useState(null);
  // Iter122a · Health-check email giornaliero
  const [hcCfg, setHcCfg] = useState({ enabled: false, ora: "07:00", email_test: "" });
  const [hcDests, setHcDests] = useState([]);
  const [hcLogs, setHcLogs] = useState([]);
  const [hcBusy, setHcBusy] = useState(false);
  const [hcSaving, setHcSaving] = useState(false);
  const [pushDiag, setPushDiag] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushTestBusy, setPushTestBusy] = useState(false);
  const [pushTestRes, setPushTestRes] = useState(null);
  const [invitaRole, setInvitaRole] = useState("entrambi");
  const [invitaBusy, setInvitaBusy] = useState(false);
  const [defaultSdi, setDefaultSdi] = useState("");
  const [crediusGiorni, setCrediusGiorni] = useState(30);
  const [crediusBusy, setCrediusBusy] = useState(false);
  const [sdiBusy, setSdiBusy] = useState(false);
  const [commBusy, setCommBusy] = useState(false);

  const exportComunicazioni = async () => {
    setCommBusy(true);
    try {
      const res = await api.get("/admin/export-comunicazioni", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `Ginius_modelli_comunicazione.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel scaricato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Download non riuscito");
    } finally { setCommBusy(false); }
  };

  const loadDefaultSdi = async () => {
    try { const { data } = await api.get("/settings"); setDefaultSdi(data.default_codice_sdi || ""); setCrediusGiorni(data.credius_sollecito_giorni || 30); } catch {}
  };
  const saveCrediusGiorni = async () => {
    const n = parseInt(crediusGiorni, 10);
    if (!(n >= 1 && n <= 365)) { toast.error("Indica un valore tra 1 e 365 giorni"); return; }
    setCrediusBusy(true);
    try {
      await api.put("/settings", { credius_sollecito_giorni: n });
      toast.success("Sollecito aggiornamento Credius salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    setCrediusBusy(false);
  };
  const saveDefaultSdi = async () => {
    const val = (defaultSdi || "").trim().toUpperCase();
    if (val && ![6, 7].includes(val.length)) { toast.error("Il codice deve avere 6 o 7 caratteri alfanumerici"); return; }
    setSdiBusy(true);
    try {
      await api.put("/settings", { default_codice_sdi: val });
      toast.success("Codice univoco SDI di default salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    setSdiBusy(false);
  };

  const loadPushDiag = async () => {
    setPushBusy(true);
    try { const { data } = await api.get("/admin/push-diagnostica"); setPushDiag(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore diagnostica push"); }
    finally { setPushBusy(false); }
  };

  const invitaNonIscritti = async () => {
    setInvitaBusy(true);
    try {
      const { data } = await api.post(`/admin/push/invita-non-iscritti?role=${invitaRole}`);
      if (data.inviati > 0) toast.success(data.message);
      else toast.info(data.message || "Nessun destinatario da invitare: sono già tutti iscritti.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio inviti non riuscito"); }
    finally { setInvitaBusy(false); }
  };

  const sendPushTest = async () => {
    setPushTestBusy(true); setPushTestRes(null);
    try {
      const { data } = await api.post("/push/test");
      setPushTestRes(data);
      if (data.ok && (!data.errori || data.errori.length === 0)) toast.success(data.message);
      else if (data.ok) toast.warning(data.message);
      else toast.error(data.message);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio di prova non riuscito"); }
    finally { setPushTestBusy(false); }
  };

  const checkEmailCfg = async () => {
    setEmailBusy(true);
    try { const { data } = await api.get("/admin/email-config"); setEmailCfg(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore diagnostica"); }
    setEmailBusy(false);
  };
  const sendEmailTest = async () => {
    if (!emailTo.includes("@")) { toast.error("Inserisci un indirizzo email valido"); return; }
    setEmailBusy(true); setEmailTestRes(null);
    try {
      const { data } = await api.post("/admin/email-test", { to: emailTo });
      setEmailTestRes(data);
      if (data.mailjet_status === "success") toast.success(`Mailjet ha accettato l'invio (a ${data.inviato_a}). Controlla la casella e Mailjet.`);
      else toast.error(`Invio non riuscito: ${data.errore || data.mailjet_status || "errore sconosciuto"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Test non riuscito"); }
    setEmailBusy(false);
  };

  const load = () => api.get("/admin/accounts").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento"));
  const loadHealthcheck = () => api.get("/settings/email-healthcheck").then((r) => {
    setHcCfg({ enabled: !!r.data.config?.enabled, ora: r.data.config?.ora || "07:00", email_test: r.data.config?.email_test || "" });
    setHcDests(r.data.destinatari_attuali || []);
    setHcLogs(r.data.logs || []);
  }).catch(() => {});
  // Iter122bb · trial/richieste/proposta ora esclusivi di SuperAdmin.
  useEffect(() => {
    load();
    loadDefaultSdi();
    loadHealthcheck();
    // Iter122bf · deep link ?section=email-prefs → scrolla alla card preferenze
    if (typeof window !== "undefined") {
      const section = new URLSearchParams(window.location.search).get("section");
      if (section === "email-prefs") {
        setTimeout(() => {
          document.getElementById("email-prefs")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 500);
      }
    }
  }, []);

  const saveHealthcheck = async () => {
    setHcSaving(true);
    try {
      await api.put("/settings/email-healthcheck", { enabled: hcCfg.enabled, ora: hcCfg.ora, email_test: hcCfg.email_test || null });
      toast.success("Impostazioni health-check salvate");
      loadHealthcheck();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore salvataggio"); }
    setHcSaving(false);
  };

  const runHealthcheckNow = async () => {
    setHcBusy(true);
    try {
      const { data } = await api.post("/settings/email-healthcheck/run-now");
      if (data.ok) toast.success(`Test inviato a ${data.destinatari?.length || 0} destinatario(i). Controlla la posta.`);
      else toast.error(`Test FALLITO: ${data.errore || "verifica configurazione"}`);
      loadHealthcheck();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore esecuzione test"); }
    setHcBusy(false);
  };

  const createCollab = async () => {
    if (!collab.name.trim() || !collab.email.trim() || collab.password.length < 6) { toast.error("Compila nome, email e password (min 6)"); return; }
    setBusy(true);
    try { await api.post("/admin/collaboratori", collab); toast.success("Collaboratore creato"); setCollab({ name: "", email: "", phone: "", password: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Creazione non riuscita"); }
    setBusy(false);
  };

  const toggle = async (a) => { try { await api.post(`/admin/accounts/${a.id}/toggle`); load(); } catch { toast.error("Operazione non riuscita"); } };
  const resetPw = async (a) => {
    const pw = window.prompt(`Nuova password per ${a.name}:`);
    if (!pw) return;
    if (pw.length < 8) { toast.error("Almeno 8 caratteri"); return; }
    try { await api.post(`/admin/accounts/${a.id}/reset-password`, { password: pw }); toast.success("Password aggiornata"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const resetLogin = async (a) => {
    const nuova = window.prompt(`Nuovo ID utente (email di login) per ${a.name}:`, a.email || "");
    if (nuova == null) return;
    const email = String(nuova).trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Email non valida"); return; }
    if (email === (a.email || "").toLowerCase()) { toast("Nessuna modifica"); return; }
    try {
      await api.post(`/admin/accounts/${a.id}/reset-login`, { email });
      toast.success("ID utente aggiornato");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore");
    }
  };
  const elimina = async (a) => {
    if (!window.confirm(`Eliminare il collaboratore "${a.name}"?`)) return;
    try { await api.delete(`/admin/accounts/${a.id}`); toast.success("Account eliminato"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center"><SlidersHorizontal className="w-7 h-7 text-white" /></div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Impostazioni &amp; Account</h1>
            <InfoHint section="pannello" />
          </div>
          <p className="text-base text-slate-500">Configurazione dello studio, licenze, moduli, dati fiscali e branding — tutto in un unico posto.</p>
        </div>
      </div>

      {/* Iter122dv · Menu a bandiera "Impostazioni & Account" — 2 gruppi (Pannello account / Impostazioni) */}
      <ImpAccMenu activeTab={activeTab} setActiveTab={setActiveTab} isPlatformAdmin={isPlatformAdmin} user={user} />

      <div className="mt-6 space-y-8">
      {/* PREFERENZE EMAIL — Iter122bf · disponibile per tutti gli admin studio (tenant-scoped) · Iter122eh · include suggerimento email dedicata */}
      {activeTab === "notifiche-email" && (
      <Collapsible titleIcon={Mail} title="Preferenze notifiche via email" testid="tab-notifiche-email" defaultOpen={true}>
        <EmailPolicyBanner user={user} />
        <EmailPreferencesCard />
      </Collapsible>
      )}

      {/* MITTENTE EMAIL — Iter122ex · visibile a tutti i tenant admin (upsell interno per Trial) */}
      {activeTab === "mittente-email" && user?.role === "admin" && (
      <section id="email-sender">
        <div className="flex items-center gap-2 mb-3"><MailOpen className="w-5 h-5 text-brand" /><h2 className="font-display text-xl font-bold">Mittente e-mail</h2></div>
        <EmailSenderCard />
      </section>
      )}

      {/* MODULI FACOLTATIVI · Iter122eh · SOLO admin Izzo/super_admin (non visibile ai tenant/trial) */}
      {activeTab === "moduli-aggiuntivi" && isPlatformAdmin && (
      <section id="moduli-opzionali">
        <ModuliFacoltativiCard />
      </section>
      )}

      {/* LICENZE CONDOMÌNI — Iter122ck · collassabile · Iter122eh · rimossa sotto-scheda "Aggiunta condomìni" (dava un warning rosso fuorviante per Trial/Light e duplicava il flusso Upgrade) · Iter122el · fix typo "condòmini" (persone) → "condomìni" (fabbricati) */}
      {activeTab === "licenze-condomini" && user?.role === "admin" && (
      <Collapsible titleIcon={Building2} title="Le mie licenze Condomìni" testid="tab-licenze-condomini" defaultOpen={true}>
        {/* Iter122dx · V2 workflow attivazione (dialog Attiva Condominio) · Iter122ef · include flusso Upgrade */}
        <LicenzeCondominiV2 />
      </Collapsible>
      )}

      {/* DIAGNOSTICA EMAIL — Iter122av · solo admin Studio Izzo o super_admin */}
      {activeTab === "diagnostica-email" && isPlatformAdmin && (
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-3"><Mail className="w-5 h-5 text-brand" /><h2 className="font-display text-xl font-bold">Diagnostica Email</h2></div>
        <Card className="p-5 border-2" data-testid="email-diag-card">
          <p className="text-sm text-slate-500 mb-3">Verifica la configurazione email dell'ambiente corrente e prova un invio reale. Utile per controllare la <b>produzione</b> dopo il deploy.</p>
          <div className="flex flex-wrap gap-3 mb-4">
            <Button data-testid="email-cfg-btn" variant="outline" disabled={emailBusy} onClick={checkEmailCfg}>
              <ShieldCheck className="w-4 h-4 mr-2" /> Verifica configurazione
            </Button>
            <div className="flex gap-2 flex-1 min-w-[260px]">
              <Input data-testid="email-test-to" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="email per il test (es. tua@email.it)" className="h-11" />
              <Button data-testid="email-test-btn" disabled={emailBusy} onClick={sendEmailTest} className="h-11 bg-brand hover:bg-brand-dark font-bold whitespace-nowrap">
                <Mail className="w-4 h-4 mr-2" /> Invia test
              </Button>
            </div>
          </div>
          {emailCfg && (
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm bg-slate-50 rounded-lg p-4" data-testid="email-cfg-result">
              <Row label="Credenziali Mailjet (SMTP_USER)" ok={emailCfg.smtp_user_set} />
              <Row label="Credenziali Mailjet (SMTP_PASSWORD)" ok={emailCfg.smtp_password_set} />
              <Row label="Autenticazione API Mailjet" ok={emailCfg.mailjet_api_auth === "ok"} text={emailCfg.mailjet_api_auth} />
              <div><span className="text-slate-500">Mittente:</span> <b>{emailCfg.email_from || "—"}</b></div>
              <div><span className="text-slate-500">Modalità sicura (SAFE_MODE):</span> <b className={emailCfg.email_safe_mode === "true" ? "text-amber-700" : "text-green-700"}>{emailCfg.email_safe_mode || "—"}</b></div>
              <div><span className="text-slate-500">Reindirizzo a:</span> <b>{emailCfg.email_safe_mode === "true" ? (emailCfg.safe_email_to || "—") : "nessuno"}</b></div>
              <div><span className="text-slate-500">Trasporto:</span> <b>{emailCfg.email_transport}</b></div>
              {emailCfg.smtp_user_hint && <div><span className="text-slate-500">Chiave Mailjet:</span> <b>{emailCfg.smtp_user_hint}</b></div>}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3">In <b>produzione</b> per recapitare ai destinatari reali: <code>EMAIL_SAFE_MODE=false</code> e credenziali Mailjet impostate. Se "Credenziali Mailjet" risulta ✗, le variabili non sono lette in quell'ambiente.</p>
          <p className="text-xs text-amber-600 mt-1">⚠️ In modalità test (anteprima) gli invii in blocco (avvisi a tutti, solleciti, credenziali massive) sono limitati a <b>1 destinatario</b> per non saturare il limite giornaliero di Mailjet (piano base: 200 email/giorno).</p>
          {emailTestRes && (
            <div className="mt-4 text-sm bg-slate-50 rounded-lg p-4 space-y-1" data-testid="email-test-result">
              <div className="font-bold mb-1">Esito test invio</div>
              <Row label={`Stato Mailjet: ${emailTestRes.mailjet_status || "—"}`} ok={emailTestRes.mailjet_status === "success"} />
              <div><span className="text-slate-500">HTTP:</span> <b>{emailTestRes.http_status ?? "—"}</b> · <span className="text-slate-500">Inviato a:</span> <b>{emailTestRes.inviato_a}</b></div>
              {emailTestRes.message_id && <div><span className="text-slate-500">MessageID:</span> <b>{emailTestRes.message_id}</b></div>}
              {emailTestRes.errore && <div className="text-red-700"><span className="font-semibold">Errore:</span> {emailTestRes.errore}</div>}
              {emailTestRes.mailjet_status === "success" && <div className="text-slate-500">Accettato da Mailjet. Se non arriva, controlla in Mailjet se risulta "blocked"/"bounce" per quel destinatario.</div>}
            </div>
          )}
        </Card>
      </section>
      )}

      {/* HEALTH-CHECK EMAIL GIORNALIERO — Iter122a · Iter122av visibilità limitata */}
      {activeTab === "healthcheck-email" && isPlatformAdmin && (
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-3"><Heart className="w-5 h-5 text-brand" /><h2 className="font-display text-xl font-bold">Health-check email giornaliero</h2></div>
        <Card className="p-5 border-2" data-testid="hc-email-card">
          <p className="text-sm text-slate-500 mb-4">
            Ogni giorno all&apos;ora impostata invia automaticamente un test (plain + HTML) al destinatario TEST configurato e ai contatti flaggati come <b>&laquo;email di test&raquo;</b> in anagrafica.
            Se un invio fallisce, arriva un alert all&apos;admin (in-app + email). Ideale per intercettare eventuali problemi di consegna Mailjet subito.
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <label className="flex items-center gap-2 h-11 px-3 rounded-lg border-2 border-slate-200 bg-slate-50 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 accent-brand" checked={hcCfg.enabled}
                onChange={(e) => setHcCfg((s) => ({ ...s, enabled: e.target.checked }))} data-testid="hc-enabled" />
              <span className="text-sm font-semibold">{hcCfg.enabled ? "Attivo" : "Disattivo"}</span>
            </label>
            <Input type="time" value={hcCfg.ora} onChange={(e) => setHcCfg((s) => ({ ...s, ora: e.target.value }))} className="h-11" data-testid="hc-ora" />
            <Input type="email" placeholder="Email TEST fisso (fallback)" value={hcCfg.email_test} onChange={(e) => setHcCfg((s) => ({ ...s, email_test: e.target.value }))} className="h-11" data-testid="hc-email" />
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={saveHealthcheck} disabled={hcSaving} className="bg-brand hover:bg-brand-dark" data-testid="hc-save">
              {hcSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva
            </Button>
            <Button variant="outline" onClick={runHealthcheckNow} disabled={hcBusy} data-testid="hc-run-now">
              {hcBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />} Prova ora
            </Button>
          </div>
          <div className="mb-4">
            <div className="text-xs font-semibold text-slate-500 mb-1">Destinatari test attuali</div>
            {hcDests.length === 0 ? (
              <div className="text-sm text-amber-700 italic">Nessuno. Imposta un&apos;email TEST fissa oppure segna un contatto come &laquo;email di test&raquo; nell&apos;anagrafica.</div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="hc-dests">
                {hcDests.map((d, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded-full bg-slate-100 border border-slate-300 text-slate-700">
                    {d.nome} · {d.email}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-2">Ultimi 7 giorni · Storico esecuzioni</div>
            {hcLogs.length === 0 ? (
              <div className="text-sm text-slate-400 italic">Nessuna esecuzione recente.</div>
            ) : (
              <div className="space-y-1" data-testid="hc-logs">
                {hcLogs.map((l) => (
                  <div key={l.id} className={`flex flex-wrap items-center gap-3 text-xs px-3 py-2 rounded-lg border-2 ${l.ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
                    {l.ok ? <Check className="w-4 h-4 text-green-700" /> : <X className="w-4 h-4 text-red-700" />}
                    <span className="font-mono">{new Date(l.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                    <span className="text-slate-500">{l.triggered_by}</span>
                    <span>Plain <b>{l.esito_plain?.inviate ?? 0}/{l.esito_plain?.totali ?? 0}</b></span>
                    <span>HTML <b>{l.esito_html?.inviate ?? 0}/{l.esito_html?.totali ?? 0}</b></span>
                    {l.errore && <span className="text-red-700 italic">· {l.errore}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </section>
      )}

      {/* DIAGNOSTICA NOTIFICHE PUSH — Iter122dv · SOLO admin Studio Izzo/super_admin */}
      {activeTab === "diagnostica-push" && isPlatformAdmin && (
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-3"><Bell className="w-5 h-5 text-brand" /><h2 className="font-display text-xl font-bold">Diagnostica Notifiche Push</h2></div>
        <Card className="p-5 border-2" data-testid="push-diag-card">
          <p className="text-sm text-slate-500 mb-3">Mostra quali utenti hanno <b>attivato le notifiche push sul dispositivo</b>. Se un condòmino/fornitore non riceve le notifiche sul cellulare, deve risultare "Iscritto" qui. Su iPhone è necessario <b>installare l'app nella schermata Home</b> e attivarle da lì.</p>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="push-diag-btn" variant="outline" disabled={pushBusy} onClick={loadPushDiag}>
              <Bell className="w-4 h-4 mr-2" /> {pushBusy ? "Carico..." : "Verifica sottoscrizioni push"}
            </Button>
            <Button data-testid="push-test-btn" disabled={pushTestBusy} onClick={sendPushTest}
              className="bg-brand hover:bg-brand-dark">
              <Bell className="w-4 h-4 mr-2" /> {pushTestBusy ? "Invio..." : "Invia notifica di prova a me stesso"}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2">Prima attiva gli avvisi sul tuo telefono (campanella → "Attiva avvisi sul telefono"; su iPhone aggiungi l'app alla Home), poi premi il pulsante di prova e controlla che la notifica arrivi sul telefono.</p>
          {pushTestRes && (
            <div data-testid="push-test-result"
              className={`mt-3 p-3 rounded-lg border-2 text-sm ${pushTestRes.ok && (!pushTestRes.errori || pushTestRes.errori.length === 0) ? "bg-green-50 border-green-200 text-green-800" : "bg-amber-50 border-amber-300 text-amber-900"}`}>
              <div className="font-semibold">{pushTestRes.message}</div>
              {pushTestRes.errori && pushTestRes.errori.length > 0 && (
                <ul className="mt-2 list-disc pl-5 space-y-1 font-mono text-xs break-words">
                  {pushTestRes.errori.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-sm font-bold text-slate-700 mb-1">Invita chi non ha ancora attivato le notifiche</p>
            <p className="text-xs text-slate-500 mb-3">Invia un'email con le istruzioni (Android e iPhone) ai destinatari ancora <b>non iscritti</b> alle push. Chi è già iscritto viene saltato.</p>
            <div className="flex flex-wrap items-center gap-2">
              <select data-testid="invita-role-select" value={invitaRole} onChange={(e) => setInvitaRole(e.target.value)}
                className="h-10 px-3 rounded-lg border-2 border-slate-200 text-sm bg-white">
                <option value="entrambi">Condòmini e fornitori</option>
                <option value="condomino">Solo condòmini</option>
                <option value="fornitore">Solo fornitori</option>
              </select>
              <Button data-testid="invita-non-iscritti-btn" variant="outline" disabled={invitaBusy} onClick={invitaNonIscritti}>
                <Bell className="w-4 h-4 mr-2" /> {invitaBusy ? "Invio inviti..." : "Invia istruzioni ai non iscritti"}
              </Button>
            </div>
          </div>
          {pushDiag && (
            <div className="mt-4" data-testid="push-diag-result">
              <div className="grid sm:grid-cols-3 gap-3 mb-4">
                {["admin", "condomino", "fornitore"].map((r) => (
                  <div key={r} className="bg-slate-50 rounded-lg p-3 text-sm">
                    <div className="font-bold capitalize">{r === "admin" ? "Studio" : r}</div>
                    <div className="text-2xl font-extrabold text-brand">{pushDiag.riepilogo[r]?.iscritti || 0}<span className="text-base text-slate-400">/{pushDiag.riepilogo[r]?.totali || 0}</span></div>
                    <div className="text-xs text-slate-500">iscritti alle push</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-slate-400 mb-2">VAPID attivo: <b className={pushDiag.vapid_attivo ? "text-green-700" : "text-red-700"}>{pushDiag.vapid_attivo ? "sì" : "no"}</b> · Totale dispositivi iscritti: <b>{pushDiag.totale_subscriptions}</b></div>
              <div className="max-h-80 overflow-auto border-2 border-slate-100 rounded-lg divide-y">
                {pushDiag.utenti.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`push-user-${u.email}`}>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{u.name} <span className="text-xs text-slate-400">({u.role === "admin" ? "Studio" : u.role})</span></div>
                      <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    </div>
                    {u.subscriptions > 0
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold shrink-0">Iscritto ({u.subscriptions})</span>
                      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-xs font-bold shrink-0">Non iscritto</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </section>
      )}

      {/* SCHEDE INFORMATIVE & ANTEPRIMA EMAIL */}
      {/* Schede informative & email di attivazione: spostate in Raccolta dati → Modelli di comunicazione e moduli */}

      {/* IMPOSTAZIONI FATTURAZIONE — Iter122dv · SOLO Izzo/super_admin (i tenant lo indicano in scheda Condominio) */}
      {activeTab === "impostazioni-fatturazione" && isPlatformAdmin && (
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-3"><Building2 className="w-5 h-5 text-brand" /><h2 className="font-display text-xl font-bold">Impostazioni fatturazione</h2></div>
        <Card className="p-5 border-2 max-w-2xl" data-testid="settings-fatturazione-card">
          <p className="text-sm text-slate-500 mb-3">
            Codice destinatario <b>SDI</b> di <b>default</b> usato nel campo "Dati fatturazione" dell'editor Conferma preventivo
            quando il condominio non ha un proprio codice SDI impostato. Modificalo qui: nessun redeploy richiesto.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
            <div className="flex-1 w-full">
              <label className="text-sm font-semibold text-slate-600">Codice univoco SDI di default</label>
              <Input data-testid="default-sdi-input" value={defaultSdi} onChange={(e) => setDefaultSdi(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7))}
                placeholder="Es. M5UXCR1" className="h-11 mt-1 uppercase tracking-widest font-mono" />
              <p className="text-xs text-slate-400 mt-1">6 caratteri (PA) o 7 caratteri (privati) alfanumerici.</p>
            </div>
            <Button data-testid="save-default-sdi" onClick={saveDefaultSdi} disabled={sdiBusy}
              className="h-11 bg-brand hover:bg-brand-dark font-bold">
              <Save className="w-4 h-4 mr-2" /> Salva
            </Button>
          </div>
        </Card>
      </section>
      )}

      {/* CREDIUS Solleciti — Iter122av: verrà spostato in Credius Dashboard, per ora nascosto */}
      {false && (
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-3"><Building2 className="w-5 h-5 text-brand" /><h2 className="font-display text-xl font-bold">Credius — Solleciti di aggiornamento</h2></div>
        <Card className="p-5 border-2 max-w-2xl" data-testid="settings-credius-card">
          <p className="text-sm text-slate-500 mb-3">
            Se una pratica legale <b>In gestione</b> non riceve aggiornamenti dall'avvocato per più dei giorni indicati,
            il sistema invia automaticamente (ore 09:50) un <b>sollecito di informativa</b> via email e notifica
            (ripetuto al massimo ogni 7 giorni).
          </p>
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
            <div className="flex-1 w-full">
              <label className="text-sm font-semibold text-slate-600">Giorni senza aggiornamenti prima del sollecito</label>
              <Input data-testid="credius-giorni-input" type="number" min="1" max="365" value={crediusGiorni}
                onChange={(e) => setCrediusGiorni(e.target.value)} className="h-11 mt-1 max-w-[140px]" />
            </div>
            <Button data-testid="save-credius-giorni" onClick={saveCrediusGiorni} disabled={crediusBusy}
              className="h-11 bg-brand hover:bg-brand-dark font-bold">
              <Save className="w-4 h-4 mr-2" /> Salva
            </Button>
            <Button data-testid="run-credius-solleciti" variant="outline" disabled={crediusBusy}
              onClick={async () => {
                setCrediusBusy(true);
                try {
                  const { data } = await api.post("/credius/run-solleciti");
                  toast.success(`Giro solleciti eseguito: ${data.inviati} inviati (limite ${data.giorni_limite} gg)`);
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Esecuzione non riuscita"); }
                setCrediusBusy(false);
              }} className="h-11">Esegui ora</Button>
          </div>
        </Card>
      </section>
      )}

      {/* MODELLI DI COMUNICAZIONE — Iter122dv · SOLO Izzo/super_admin */}
      {activeTab === "modelli-comunicazione" && isPlatformAdmin && (
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-3"><FileSpreadsheet className="w-5 h-5 text-brand" /><h2 className="font-display text-xl font-bold">Modelli di comunicazione</h2></div>
        <Card className="p-5 border-2 max-w-2xl" data-testid="settings-comunicazioni-card">
          <p className="text-sm text-slate-500 mb-3">
            Esporta in un file Excel <b>tutti i modelli di email/notifica</b> trasmessi dall'app
            (a Studio, fornitori e condòmini), suddivisi per funzione e destinatario. Il file include
            oggetto, corpo, link/CTA disponibili e suggerimenti di miglioramento — pronto per essere
            editato manualmente o con AI e re-importato per aggiornare i template in blocco.
          </p>
          <Button data-testid="export-comunicazioni-btn" onClick={exportComunicazioni} disabled={commBusy}
            className="h-11 bg-brand hover:bg-brand-dark font-bold">
            <Download className="w-4 h-4 mr-2" /> {commBusy ? "Generazione..." : "Scarica Excel modelli"}
          </Button>
        </Card>
      </section>
      )}

      {/* GESTIONE UTENTI APP — Iter122dv · Ex "Collaboratori dello Studio" */}
      {activeTab === "gestione-utenti" && (
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-3"><Users className="w-5 h-5 text-brand" /><h2 className="font-display text-xl font-bold">Gestione utenti APP</h2></div>
        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="p-5 border-2 h-fit" data-testid="collab-form">
            <div className="flex items-center gap-2 mb-3 font-bold text-slate-700"><UserPlus className="w-5 h-5" /> Nuovo collaboratore</div>
            <Field label="Nome e cognome" required data-testid="collab-name" value={collab.name} onChange={(e) => setCollab({ ...collab, name: e.target.value })} placeholder="Es. Giulia Verdi" />
            <Field label="Email" required data-testid="collab-email" type="email" value={collab.email} onChange={(e) => setCollab({ ...collab, email: e.target.value })} placeholder="nome@amministrazionizzo.com" />
            <Field label="Telefono (opzionale)" data-testid="collab-phone" value={collab.phone} onChange={(e) => setCollab({ ...collab, phone: e.target.value })} />
            <Field label="Password" required data-testid="collab-password" type="text" value={collab.password} onChange={(e) => setCollab({ ...collab, password: e.target.value })} placeholder="min 8 caratteri" />
            <Button data-testid="collab-submit" disabled={busy} onClick={createCollab} className="w-full h-11 bg-brand hover:bg-brand-dark font-bold mt-1"><UserPlus className="w-5 h-5 mr-2" /> Crea collaboratore</Button>
            <p className="text-xs text-slate-400 mt-3">Accesso completo ai dati reali dello Studio, come gli amministratori.</p>
          </Card>
          <div className="lg:col-span-2 space-y-3" data-testid="collab-list">
            {data.collaboratori.length === 0 && <Card className="p-4 text-center text-slate-500">Nessun collaboratore aggiuntivo.</Card>}
            {data.collaboratori.map((a) => (
              <Card key={a.id} className="p-4 border-2 flex flex-wrap items-center justify-between gap-3" data-testid={`collab-${a.id}`}>
                <div className="min-w-0">
                  <div className="font-bold text-slate-800">{a.name} {a.disabilitato && <span className="text-xs text-red-600 font-semibold">· disabilitato</span>}</div>
                  <div className="text-sm text-slate-500">{a.email} {a.phone ? `· ${a.phone}` : ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => resetLogin(a)} data-testid={`collab-login-${a.id}`} title="Modifica ID utente (email di login)"><AtSign className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => resetPw(a)} data-testid={`collab-reset-${a.id}`} title="Reset password"><KeyRound className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => toggle(a)} data-testid={`collab-toggle-${a.id}`} title={a.disabilitato ? "Riabilita" : "Disabilita"} className={a.disabilitato ? "text-green-700" : "text-amber-700"}><Power className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => elimina(a)} data-testid={`collab-del-${a.id}`} title="Elimina" className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* PROFILO STUDIO · Iter122dv · sempre visibile al tenant */}
      {activeTab === "profilo-studio" && <ProfileStudioCard />}

      {/* LOGO E FIRMA · Iter122dv · sempre visibile al tenant */}
      {activeTab === "logo-firma" && <LogoFirmaCard />}

      {/* AUTOMAZIONI NOTIFICHE · Iter122k99 · console solleciti/push per tenant */}
      {activeTab === "automazioni-notifiche" && (
        <Collapsible titleIcon={BellOff} title="Automazioni notifiche / solleciti" testid="tab-automazioni-notifiche" defaultOpen={true}>
          <AutomazioniNotificheCard />
        </Collapsible>
      )}

      </div>
    </div>
  );
}

/* Iter122dv · Menu a bandiera "Impostazioni & Account" — 2 gruppi orizzontali con voci verticali */
function ImpAccMenu({ activeTab, setActiveTab, isPlatformAdmin, user }) {
  const isTenantAdmin = user?.role === "admin";
  const groups = [
    { label: "Pannello account", items: [
      { key: "notifiche-email", label: "Preferenze notifiche via email", icon: Mail, forAll: true },
      { key: "moduli-aggiuntivi", label: "Moduli aggiuntivi", icon: Package, izzoOnly: true },
      { key: "licenze-condomini", label: "Le mie licenze Condomìni", icon: Building2, forAll: true },
      { key: "diagnostica-email", label: "Diagnostica Email", icon: Mail, izzoOnly: true },
      { key: "healthcheck-email", label: "Health-check email", icon: Heart, izzoOnly: true },
      { key: "diagnostica-push", label: "Diagnostica Notifiche Push", icon: Bell, izzoOnly: true },
      { key: "gestione-utenti", label: "Gestione utenti APP", icon: Users, forAll: true },
    ]},
    { label: "Impostazioni", items: [
      // Iter122ex · "Mittente e-mail" ora visibile a TUTTI i tenant admin (non più izzo-only):
      // ogni studio potrà scegliere il proprio alias @giniusapp.it. Trial vede la tab ma con
      // upsell banner + save disabilitato (gate applicato dentro EmailSenderCard).
      { key: "mittente-email", label: "Mittente e-mail", icon: MailOpen, adminOnly: true },
      { key: "impostazioni-fatturazione", label: "Impostazioni fatturazione", icon: Receipt, izzoOnly: true },
      { key: "modelli-comunicazione", label: "Modelli di comunicazione", icon: FileSpreadsheet, izzoOnly: true },
      { key: "profilo-studio", label: "Profilo Studio · Titolare del trattamento", icon: ShieldCheck, forAll: true },
      { key: "logo-firma", label: "Logo e firma dello Studio", icon: Palette, forAll: true },
      { key: "automazioni-notifiche", label: "Automazioni notifiche / solleciti", icon: BellOff, adminOnly: true },
    ]},
  ];
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="imp-acc-menu">
      {groups.map((g) => {
        const items = g.items.filter((i) => i.forAll || (i.izzoOnly && isPlatformAdmin) || (i.adminOnly && isTenantAdmin));
        if (items.length === 0) return null;
        return (
          <Card key={g.label} className="p-3 border-2">
            <div className="text-xs uppercase tracking-wide font-bold text-brand mb-2 px-1">{g.label}</div>
            <ul className="space-y-0.5">
              {items.map((it) => {
                const Icon = it.icon;
                const isActive = activeTab === it.key;
                return (
                  <li key={it.key}>
                    <button data-testid={`impacc-tab-${it.key}`}
                      onClick={() => setActiveTab(it.key)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-brand text-white" : "hover:bg-slate-100 text-slate-700"}`}>
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-white" : "text-brand"}`} />
                      <span className="truncate">{it.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>
        );
      })}
    </div>
  );
}

/* Iter122dv · wrapper collassabile riutilizzabile (default aperto o chiuso). */
function Collapsible({ titleIcon: Icon, title, testid, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="mb-10" data-testid={testid}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 mb-3 group">
        {Icon && <Icon className="w-5 h-5 text-brand" />}
        <h2 className="font-display text-xl font-bold text-slate-800 group-hover:text-brand transition-colors">{title}</h2>
        <span className="ml-auto text-xs text-slate-500 font-semibold">{open ? "▲ chiudi" : "▼ apri"}</span>
      </button>
      {open && children}
    </section>
  );
}
